import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Clock, ListChecks, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate, formatDateOnly } from "@/lib/cn";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { QueryError } from "@/components/QueryError";
import { AdminRecordActions } from "@/components/AdminRecordActions";
import { StaffFormModal } from "./StaffFormModal";
import { ForceDeleteDialog, isForceable, type Dependants } from "@/components/ForceDeleteDialog";

type TaskStatus = "PENDING" | "IN_PROGRESS" | "DONE";
type Priority = "LOW" | "MEDIUM" | "HIGH" | "URGENT";

interface TimeEntry {
  id: string;
  date: string;
  clockIn: string;
  clockOut?: string | null;
  hoursWorked?: string | null;
  notes?: string | null;
}

interface Task {
  id: string;
  title: string;
  description?: string | null;
  priority: Priority;
  dueDate?: string | null;
  status: TaskStatus;
  completedAt?: string | null;
}

interface StaffDetail {
  id: string;
  firstName: string;
  lastName: string;
  position: string;
  startDate: string;
  isActive: boolean;
  email?: string | null;
  phone?: string | null;
  description?: string | null;
  assignedBuildings?: string[];
  timeEntries: TimeEntry[];
  tasks: Task[];
}

const priorityVariant: Record<Priority, "default" | "info" | "warning" | "danger"> = {
  LOW: "default",
  MEDIUM: "info",
  HIGH: "warning",
  URGENT: "danger",
};

const COLUMNS: { status: TaskStatus; titleKey: string }[] = [
  { status: "PENDING", titleKey: "hr.taskStatus.PENDING" },
  { status: "IN_PROGRESS", titleKey: "hr.taskStatus.IN_PROGRESS" },
  { status: "DONE", titleKey: "hr.taskStatus.DONE" },
];

export function StaffDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [timeOpen, setTimeOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const { data: staff, isLoading, isError } = useQuery({
    queryKey: ["staff", id],
    queryFn: async () => (await api.get<StaffDetail>(`/staff/${id}`)).data,
    enabled: !!id,
  });

  const taskMutation = useMutation({
    mutationFn: async ({ taskId, status }: { taskId: string; status: TaskStatus }) =>
      (await api.put(`/staff/${id}/tasks/${taskId}`, { status })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff", id] }),
  });

  const closeEntryMutation = useMutation({
    mutationFn: async (entryId: string) =>
      (await api.put(`/staff/${id}/time-entry/${entryId}`, { clockOut: new Date().toISOString() })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff", id] }),
  });

  const deleteTaskMutation = useMutation({
    mutationFn: async (taskId: string) => (await api.delete(`/staff/${id}/tasks/${taskId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff", id] }),
    onError: (e: any) => alert(e?.response?.data?.message ?? "Error"),
  });

  const [forceTarget, setForceTarget] = useState<Dependants | null>(null);
  const deleteMutation = useMutation({
    mutationFn: async ({ staffId, force }: { staffId: string; force?: boolean }) =>
      (await api.delete(`/staff/${staffId}${force ? "?force=true" : ""}`)).data,
    onSuccess: () => {
      setForceTarget(null);
      qc.invalidateQueries({ queryKey: ["staff"] });
      navigate("/admin/staff");
    },
    onError: (e: any, vars) => {
      const dependants = isForceable(e);
      if (dependants && !vars.force) {
        setForceTarget(dependants);
        return;
      }
      alert(e?.response?.data?.message ?? "Error");
    },
  });

  if (isError) return <QueryError />;
  if (isLoading || !staff) return <div className="text-slate-500">{t("common.loading")}</div>;

  const totalHours = staff.timeEntries.reduce((s, e) => s + parseFloat(e.hoursWorked ?? "0"), 0);

  return (
    <div className="space-y-6">
      <Link to="/admin/staff" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
        <ArrowLeft size={14} /> {t("hr.staff")}
      </Link>

      <div className="card">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">{staff.firstName} {staff.lastName}</h1>
            <p className="text-slate-600">{staff.position}</p>
            <div className="text-sm text-slate-500 mt-2 flex flex-wrap gap-4">
              <span>{t("hr.startDate")}: {formatDateOnly(staff.startDate, i18n.language)}</span>
              {staff.email && <span>{staff.email}</span>}
              {staff.phone && <span>{staff.phone}</span>}
            </div>
            {!!staff.assignedBuildings?.length && (
              <div className="mt-3 flex flex-wrap items-center gap-1.5 text-sm">
                <span className="text-slate-500">{t("hr.assignedBuildings")}:</span>
                {staff.assignedBuildings.map((b) => (
                  <span key={b} className="text-xs px-2 py-0.5 rounded bg-primary-50 text-primary-800 border border-primary-100">
                    {b}
                  </span>
                ))}
              </div>
            )}
            {staff.description && (
              <div className="mt-3">
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{t("hr.description")}</div>
                <p className="text-sm text-slate-700 whitespace-pre-line mt-1">{staff.description}</p>
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-3">
            <Badge variant={staff.isActive ? "success" : "default"}>{staff.isActive ? t("hr.active") : t("hr.inactive")}</Badge>
            <AdminRecordActions
              entityLabel={`${staff.firstName} ${staff.lastName}`}
              onEdit={() => setEditOpen(true)}
              onDelete={() => deleteMutation.mutate({ staffId: staff.id })}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Timesheet */}
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Clock size={18} /> {t("hr.timesheet")} <span className="text-sm font-normal text-slate-500">· {totalHours.toFixed(2)}h</span>
            </h2>
            <button className="btn-secondary text-xs" onClick={() => setTimeOpen(true)}>
              <Plus size={12} /> {t("hr.addEntry")}
            </button>
          </div>
          {staff.timeEntries.length === 0 ? (
            <p className="text-sm text-slate-500">—</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left py-1">Fecha</th>
                  <th className="text-left py-1">{t("hr.clockIn")}</th>
                  <th className="text-left py-1">{t("hr.clockOut")}</th>
                  <th className="text-right py-1">{t("hr.hours")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {staff.timeEntries.map((e) => (
                  <tr key={e.id}>
                    <td className="py-1">{formatDateOnly(e.date, i18n.language)}</td>
                    <td className="py-1">{new Date(e.clockIn).toLocaleTimeString("es-PR", { hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="py-1">
                      {e.clockOut ? new Date(e.clockOut).toLocaleTimeString("es-PR", { hour: "2-digit", minute: "2-digit" })
                        : <button className="text-xs text-primary hover:underline" onClick={() => closeEntryMutation.mutate(e.id)}>Cerrar ahora</button>}
                    </td>
                    <td className="py-1 text-right font-medium">{e.hoursWorked ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Tasks summary */}
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <ListChecks size={18} /> {t("hr.tasks")}
            </h2>
            <button className="btn-secondary text-xs" onClick={() => setTaskOpen(true)}>
              <Plus size={12} /> {t("hr.newTask")}
            </button>
          </div>
          <div className="text-sm text-slate-600">
            {COLUMNS.map((col) => (
              <div key={col.status} className="flex justify-between py-1">
                <span>{t(col.titleKey)}</span>
                <span className="font-medium">{staff.tasks.filter((tk) => tk.status === col.status).length}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Task board */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {COLUMNS.map((col) => {
          const tasks = staff.tasks.filter((tk) => tk.status === col.status);
          return (
            <div key={col.status} className="bg-slate-100 rounded-lg p-3">
              <div className="font-semibold text-slate-700 mb-2 flex items-center justify-between">
                <span>{t(col.titleKey)}</span>
                <Badge>{tasks.length}</Badge>
              </div>
              <div className="space-y-2">
                {tasks.map((tk) => (
                  <div key={tk.id} className="bg-white rounded p-3 shadow-sm">
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant={priorityVariant[tk.priority]}>{tk.priority}</Badge>
                      {tk.dueDate && <span className="text-xs text-slate-500">{formatDateOnly(tk.dueDate, i18n.language)}</span>}
                    </div>
                    <div className="font-medium text-sm">{tk.title}</div>
                    {tk.description && <p className="text-xs text-slate-600 mt-1">{tk.description}</p>}
                    <div className="mt-2 flex items-center gap-1">
                      {COLUMNS.filter((c) => c.status !== tk.status).map((c) => (
                        <button
                          key={c.status}
                          className="text-xs text-primary hover:underline"
                          onClick={() => taskMutation.mutate({ taskId: tk.id, status: c.status })}
                        >
                          → {t(c.titleKey)}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="ml-auto text-xs text-red-600 hover:underline inline-flex items-center gap-1"
                        title={t("hr.deleteTask")}
                        onClick={() => {
                          if (confirm(t("hr.deleteTaskConfirm"))) deleteTaskMutation.mutate(tk.id);
                        }}
                      >
                        <Trash2 size={12} /> {t("hr.deleteTask")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <TimeEntryModal open={timeOpen} onClose={() => setTimeOpen(false)} staffId={id!} onDone={() => qc.invalidateQueries({ queryKey: ["staff", id] })} />
      <NewTaskModal open={taskOpen} onClose={() => setTaskOpen(false)} staffId={id!} onDone={() => qc.invalidateQueries({ queryKey: ["staff", id] })} />
      <ForceDeleteDialog
        open={!!forceTarget}
        dependants={forceTarget}
        pending={deleteMutation.isPending}
        onCancel={() => setForceTarget(null)}
        onConfirm={() => deleteMutation.mutate({ staffId: staff.id, force: true })}
      />
      <StaffFormModal
        open={editOpen}
        target={staff}
        onClose={() => setEditOpen(false)}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ["staff", id] });
          qc.invalidateQueries({ queryKey: ["staff"] });
        }}
      />
    </div>
  );
}

function TimeEntryModal({ open, onClose, staffId, onDone }: { open: boolean; onClose: () => void; staffId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const now = new Date();
  const [form, setForm] = useState({
    date: now.toISOString().slice(0, 10),
    clockIn: now.toISOString().slice(0, 16),
    clockOut: "",
    notes: "",
  });
  const mutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/staff/${staffId}/time-entry`, {
        date: new Date(form.date).toISOString(),
        clockIn: new Date(form.clockIn).toISOString(),
        clockOut: form.clockOut ? new Date(form.clockOut).toISOString() : null,
        notes: form.notes || null,
      })).data,
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={t("hr.addEntry")}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
        <div>
          <label className="label">Fecha</label>
          <input type="date" className="input" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("hr.clockIn")}</label>
            <input type="datetime-local" className="input" required value={form.clockIn} onChange={(e) => setForm({ ...form, clockIn: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("hr.clockOut")}</label>
            <input type="datetime-local" className="input" value={form.clockOut} onChange={(e) => setForm({ ...form, clockOut: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="label">Notas</label>
          <input className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}

function NewTaskModal({ open, onClose, staffId, onDone }: { open: boolean; onClose: () => void; staffId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "MEDIUM" as Priority,
    dueDate: "",
  });
  const mutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/staff/${staffId}/tasks`, {
        ...form,
        description: form.description || null,
        dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : null,
      })).data,
    onSuccess: () => {
      onDone();
      onClose();
      setForm({ title: "", description: "", priority: "MEDIUM", dueDate: "" });
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={t("hr.newTask")}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
        <div>
          <label className="label">{t("hr.taskTitle")}</label>
          <input className="input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div>
          <label className="label">Descripción</label>
          <textarea className="input min-h-[80px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("hr.priority")}</label>
            <select className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value as Priority })}>
              {(["LOW", "MEDIUM", "HIGH", "URGENT"] as Priority[]).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="label">{t("hr.dueDate")}</label>
            <input type="date" className="input" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}
